// Georgia State University: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Georgia State's Common Data Set 2025-2026, the Excel file linked as
//    "2025-2026 CDS" from the Office of Institutional Effectiveness page. The
//    file itself sits on Dropbox, so SOURCES links the gsu.edu page that
//    publishes it. Read from its machine-readable answer columns: C1
//    applicants/admits/enrollees for Fall 2025 (C.101 to C.130, with the
//    residency rows), C8 test policy for Fall 2027, C21/C22 plans.
//  - admissions.gsu.edu, First-Year Students (high school applicants):
//    deadlines and the test requirement for the Atlanta campus. gsu.edu sits
//    behind a Cloudflare check, so it was read in a browser.
//  - sfs.gsu.edu, 2026-2027 Cost of Attendance, Bachelor's Degree tab, on
//    campus. Two items carry cents on the page; the rows are ordered so the
//    column sums in floating point equal the published totals exactly.
//  - College Scorecard API, school id 139940 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Georgia State Common Data Set 2025-2026 (Office of Institutional Effectiveness)',
        url: 'https://oie.gsu.edu/data-reporting-systems/common-data-set/',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'Georgia State Admissions, First-Year Students',
        url: 'https://admissions.gsu.edu/bachelors-degree/apply/high-school/',
        read: '2026-09-30',
    },
    costs: {
        label: 'Georgia State Student Financial Services, 2026-2027 Cost of Attendance',
        url: 'https://sfs.gsu.edu/resources/coa/2026-2027-cost-of-attendance/',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?139940-Georgia-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'georgia-state-university',
    kbId: 'georgia_state_university',
    name: 'Georgia State University',
    shortName: 'Georgia State',
    location: 'Atlanta, GA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Georgia State web color guidelines); decoration only.
    branding: {
        primary: '#0039A6',
        secondary: '#CC0000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/georgia_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 42323,
        admitted: 24217,
        enrolled: 4470,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 20010, admitted: 13381 },
            { group: 'Out-of-state', applied: 19378, admitted: 9267 },
            { group: 'Nonresidents (international)', applied: 2935, admitted: 1569 },
        ],
        residencyWhere: 'section C1, residency rows',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Georgia State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'May 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'May 1', decision: null, binding: false },
        ],
        note: 'November 15 is also the Honors and scholarship priority deadline. Official transcripts and test scores are due June 1. The application opens August 14. Georgia State says deadlines are subject to change and it cannot promise a decision for anything received late. The page does not publish decision dates.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'If you apply to the Atlanta campus for fall 2026 or later, you must send official SAT or ACT scores. Georgia State lists minimums: a 2.75 GPA in the required high school courses, SAT scores of 480 in reading and writing and 440 in math, or ACT scores of 17 in English or reading and 17 in math. Meeting them does not guarantee admission.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, with scores due by June 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Personal expenses', amounts: [4394.38, 4394.38] },
            { item: 'Transportation', amounts: [1037.13, 1037.13] },
            { item: 'Tuition', amounts: [9270, 30900] },
            { item: 'Fees', amounts: [1320, 1320] },
            { item: 'Books and supplies', amounts: [2066, 2066] },
            { item: 'Housing (on campus)', amounts: [11864, 11864] },
            { item: 'Food', amounts: [4466, 4466] },
            { item: 'Direct loan fees', amounts: [132, 132] },
        ],
        totals: [34549.51, 56179.51],
        note: 'For a bachelor\'s degree student living on campus, fall and spring. Georgia State publishes these to the cent.',
        source: 'costs',
        netPrice: {
            average: 12365,
            byIncome: [
                { income: '$0 to $30,000', amount: 10899 },
                { income: '$30,001 to $48,000', amount: 10748 },
                { income: '$48,001 to $75,000', amount: 11521 },
                { income: '$75,001 to $110,000', amount: 15297 },
                { income: 'Over $110,000', amount: 17561 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Georgia State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
